import React, { useCallback, useLayoutEffect, useRef } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { type ChatError, type komplyanceAiChatMessage } from '@app/pages';
import { Trans } from '@libs/i18n';
import { FeedbackRating } from '@libs/types';

import { CHAT_COLUMN_GUTTER, CHAT_COLUMN_MAX_WIDTH, COMPOSER_MORPH } from '../constants';
import { type ChatAttachments, useScrollToBottom } from '../hooks';
import { consumeComposerOrigin } from '../utils/composerHandoff';

import { MessageBubble } from './MessageBubble/MessageBubble';
import { ChatComposer } from './ChatComposer';
import { ChatHeader } from './ChatHeader';
import { ChatSurface } from './ChatSurface';
import { ScrollToBottomButton } from './ScrollToBottomButton';

export interface ChatMessagesProps {
  messages: komplyanceAiChatMessage[];
  conversationId?: string;
  isLoadingMessages: boolean;
  hasLoadError: boolean;
  refetchConversation: () => void;
  refetchMessages: () => void;
  isInitialLoading: boolean;
  feedbackSubmitting: Map<string, boolean>;
  chatError?: ChatError | null;
  rateLimitCountdown?: number;
  onRetryMessage?: () => void;
  onSendMessage: () => void;
  onStopGeneration: () => void;
  onSendFeedback: (messageId: string, rating: FeedbackRating) => void;
  onSaveChatName: () => void;
  editedChatName: string;
  isEditingChatName: boolean;
  isLoadingChatNameSaving: boolean;
  onStopEditingChatName: () => void;
  onStartEditingChatName: () => void;
  onChatNameInputChange: (value: string) => void;
  chatName: string;
  endOfPageRef: React.RefObject<HTMLDivElement | null>;
}

export interface ChatNameProps {
  onSaveChatName: () => void;
  editedChatName: string;
  isEditingChatName: boolean;
  isLoadingChatNameSaving: boolean;
  onStopEditingChatName: () => void;
  onStartEditingChatName: () => void;
  onChatNameInputChange: (value: string) => void;
  chatName: string;
}

export interface ChatComposerHostProps {
  onChatInputChange: (value: string) => void;
  chatInputValue: string;
  /** Document staged for the next send. */
  attachments: ChatAttachments;
  // Deep Research mode
  deepResearchEnabled: boolean;
  onDeepResearchToggle: () => void;
}

export type ChatPageProps = ChatNameProps & ChatMessagesProps & ChatComposerHostProps;

/** Messages and the composer share one column so their edges line up. */
const columnSx = {
  width: '100%',
  maxWidth: CHAT_COLUMN_MAX_WIDTH,
  mx: 'auto',
} as const;

/** Loading / error states fill the window rather than the whole viewport. */
const centeredStateSx = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 2,
} as const;

export const ChatPage: React.FC<ChatPageProps> = observer(
  ({
    messages,
    conversationId,
    chatInputValue,
    isLoadingMessages,
    feedbackSubmitting = new Map<string, boolean>(),
    chatError,
    rateLimitCountdown = 0,
    onRetryMessage,
    onChatInputChange,
    onChatNameInputChange,
    hasLoadError,
    refetchConversation,
    refetchMessages,
    onSendMessage,
    onStopGeneration,
    onSendFeedback,
    onSaveChatName,
    editedChatName,
    isEditingChatName,
    isLoadingChatNameSaving,
    onStopEditingChatName,
    onStartEditingChatName,
    chatName = '',
    isInitialLoading,
    endOfPageRef,
    attachments,
    // Deep Research mode
    deepResearchEnabled,
    onDeepResearchToggle,
  }) => {
    const composerShellRef = useRef<HTMLDivElement | null>(null);

    const {
      showScrollButton,
      hasNewMessage,
      isScrollingDown,
      messagesContainerRef,
      handleScroll,
      scrollToBottom,
    } = useScrollToBottom({ messages, endOfPageRef });

    // Shared-element landing: when this thread was opened by sending from the
    // hero, the composer flies from where it sat there down into its dock, so
    // the two placements read as one component that moved.
    //
    // Layout effect, not a passive one: this has to be queued before the browser
    // paints, or the dock is drawn once at its final position and the flight
    // starts with a visible snap backwards.
    useLayoutEffect(() => {
      const shell = composerShellRef.current;
      if (!shell) return;

      const from = consumeComposerOrigin();
      if (!from) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      const to = shell.getBoundingClientRect();
      if (!to.width || !from.width) return;

      const dx = from.left - to.left;
      const dy = from.top - to.top;
      const scale = from.width / to.width;

      // Scale about the top-left corner so the first frame lands exactly on the
      // hero's rect. With the default centre origin the start position drifts by
      // half the size difference, which is what made the takeoff look like a jump.
      shell.animate(
        [
          {
            transformOrigin: 'top left',
            transform: `translate(${dx}px,${dy}px) scale(${scale})`,
          },
          { transformOrigin: 'top left', transform: 'none' },
        ],
        // `backwards` fill pins the start frame while the animation is still
        // pending, so the very first painted frame is already at the origin.
        { duration: COMPOSER_MORPH.durationMs, easing: COMPOSER_MORPH.easing, fill: 'backwards' },
      );

      // Opacity runs as its own, shorter animation. Folding it into the transform
      // keyframes would have forced a shared timeline and made the travel uneven.
      shell.animate([{ opacity: 0.6 }, { opacity: 1 }], {
        duration: Math.round(COMPOSER_MORPH.durationMs * 0.45),
        easing: 'ease-out',
        fill: 'backwards',
      });
    }, []);

    // After send, follow the new user message + assistant area (like ChatGPT/Claude),
    // even if the user had scrolled up to read history.
    const handleSendMessageAndScroll = useCallback(() => {
      onSendMessage();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollToBottom({ behavior: 'auto' });
        });
      });
    }, [onSendMessage, scrollToBottom]);

    if (hasLoadError) {
      const handleRetry = () => {
        refetchConversation();
        refetchMessages();
      };

      return (
        <ChatSurface>
          <Box sx={{ ...centeredStateSx, px: 2 }}>
            <Typography variant="h6" fontWeight={600}>
              <Trans i18nKey="chat.loadError.title" />
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <Trans i18nKey="chat.loadError.description" />
            </Typography>
            <Button variant="contained" onClick={handleRetry}>
              <Trans i18nKey="chat.loadError.button" />
            </Button>
          </Box>
        </ChatSurface>
      );
    }

    if (isInitialLoading) {
      return (
        <ChatSurface>
          <Box sx={centeredStateSx}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              <Trans i18nKey="chat.loading.title" />
            </Typography>
          </Box>
        </ChatSurface>
      );
    }

    return (
      <ChatSurface
        header={
          <ChatHeader
            isEditingChatName={isEditingChatName}
            isLoadingChatNameSaving={isLoadingChatNameSaving}
            editedChatName={editedChatName}
            onChatNameInputChange={onChatNameInputChange}
            onStopEditingChatName={onStopEditingChatName}
            onSaveChatName={onSaveChatName}
            chatName={chatName}
            onStartEditingChatName={onStartEditingChatName}
          />
        }
      >
        <Box
          ref={messagesContainerRef}
          onScroll={handleScroll}
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            px: CHAT_COLUMN_GUTTER,
            pt: 1,
          }}
        >
          <Box sx={columnSx}>
            {messages.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 8,
                  color: 'text.secondary',
                  fontSize: '0.875rem',
                }}
              >
                <Trans i18nKey="chat.empty.title" />
              </Box>
            ) : (
              messages.map((message) => (
                <MessageBubble
                  key={message.id}
                  message={message}
                  conversationId={conversationId}
                  onFeedback={onSendFeedback}
                  isFeedbackSubmitting={feedbackSubmitting.get(message.id)}
                />
              ))
            )}

            {chatError && (
              <Box
                sx={{
                  p: 2,
                  backgroundColor: '#FEE2E2',
                  borderRadius: 2,
                  borderInlineStart: '4px solid #DC2626',
                  color: '#DC2626',
                  marginY: 2,
                }}
              >
                <Typography variant="body2" color="inherit">
                  {chatError.message}
                </Typography>
                {rateLimitCountdown > 0 && (
                  <Typography variant="caption" sx={{ mt: 0.5, display: 'block', opacity: 0.8 }}>
                    <Trans
                      i18nKey="chat.errors.rateLimitCountdown"
                      values={{ seconds: rateLimitCountdown }}
                    />
                  </Typography>
                )}
                {chatError.recoverable && onRetryMessage && rateLimitCountdown <= 0 && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={onRetryMessage}
                    sx={{
                      mt: 1,
                      color: '#DC2626',
                      backgroundColor: 'transparent',
                      borderColor: '#DC2626',
                      '&:hover': { borderColor: '#B91C1C', backgroundColor: '#FEF2F2' },
                    }}
                  >
                    <Trans i18nKey="chat.errors.retry" />
                  </Button>
                )}
              </Box>
            )}

            <div ref={endOfPageRef} />
          </Box>
        </Box>

        <ScrollToBottomButton
          show={showScrollButton}
          hasNewMessage={hasNewMessage}
          isScrollingDown={isScrollingDown}
          onClick={scrollToBottom}
        />

        <Box sx={{ flexShrink: 0, px: CHAT_COLUMN_GUTTER, pt: 1.5, pb: 3 }}>
          <ChatComposer
            placement="docked"
            value={chatInputValue}
            onChange={onChatInputChange}
            onSend={handleSendMessageAndScroll}
            onStopGeneration={onStopGeneration}
            isStreaming={isLoadingMessages}
            disabled={rateLimitCountdown > 0}
            attachments={attachments}
            deepResearchEnabled={deepResearchEnabled}
            onDeepResearchToggle={onDeepResearchToggle}
            shellRef={composerShellRef}
          />
        </Box>
      </ChatSurface>
    );
  },
);
